import React, { useEffect, useState } from 'react';
import {
  Box, Heading, Text, SimpleGrid, Progress, Flex, Tag, Select, useColorModeValue, IconButton, Button, HStack, VStack, Icon, Divider
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { labelTipo, corTextoTipo, isEntrada, descricaoCurta } from '../utils/lancamentos';
import { ContaPagar, PagamentoConta, ocorrenciasPendentes, dataHojeLocal, diasAte } from '../utils/contas';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { IconType } from 'react-icons';

const meses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function formatarMoeda(valor: number) {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function formatarDataBR(dataStr: string) {
  if (!dataStr) return '';
  const [ano, mes, dia] = dataStr.split('-');
  return `${dia}/${mes}/${ano}`;
}

// Fora do Dashboard: definido dentro, seria um componente novo a cada render
const Cartao: React.FC<{ titulo: string; acao?: React.ReactNode; children: React.ReactNode }> = ({ titulo, acao, children }) => {
  const bg = useColorModeValue('white', 'gray.700');
  const borda = useColorModeValue('gray.100', 'gray.600');
  return (
    <Box p={4} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor={borda} bg={bg}>
      <Flex justify="space-between" align="center" mb={3} gap={2}>
        <Heading size="sm">{titulo}</Heading>
        {acao}
      </Flex>
      {children}
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [lancamentos, setLancamentos] = useState<any[]>([]);
  const [bancos, setBancos] = useState<any[]>([]);
  // Mostrar/ocultar saldos fica lembrado neste aparelho
  const [showBalance, setShowBalance] = useState(() => {
    try { return localStorage.getItem('mostrar_saldos') === '1'; } catch { return false; }
  });
  const [verTodoOrcamento, setVerTodoOrcamento] = useState(false);
  const [contas, setContas] = useState<ContaPagar[]>([]);
  const [pagamentosContas, setPagamentosContas] = useState<PagamentoConta[]>([]);
  const [planejamentos, setPlanejamentos] = useState<any[]>([]);
  const navigate = useNavigate();
  const hojeISO = dataHojeLocal();

  const alternarSaldo = () => {
    const novo = !showBalance;
    setShowBalance(novo);
    try { localStorage.setItem('mostrar_saldos', novo ? '1' : '0'); } catch { /* sem armazenamento: vale só nesta visita */ }
  };

  const EyeIcon: IconType = showBalance ? FaEye : FaEyeSlash;

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      try {
        setLancamentos(await api.get(`/api/lancamentos?mes=${mes}&ano=${ano}`));
      } catch {
        setLancamentos([]);
      }
    };
    fetchData();
  }, [user, mes, ano]);

  // Contas a pagar + pagamentos (para os lembretes de vencimento)
  useEffect(() => {
    if (!user) return;
    const fetchContas = async () => {
      try {
        const anoAtual = new Date().getFullYear();
        // A janela de lembretes olha no máximo 12 meses para trás
        const [cts, pgs] = await Promise.all([
          api.get('/api/contas-pagar'),
          api.get(`/api/contas-pagar/pagamentos?desde_ano=${anoAtual - 1}`),
        ]);
        setContas(cts);
        setPagamentosContas(pgs);
      } catch {
        // Sem os dois lados a seção não aparece: com contas mas sem pagamentos,
        // contas já pagas seriam listadas como atrasadas (alarme falso)
        setContas([]);
        setPagamentosContas([]);
      }
    };
    fetchContas();
  }, [user]);

  // Planejamento do mês (para o comparativo planejado × gasto)
  useEffect(() => {
    if (!user) return;
    const fetchPlanejamentos = async () => {
      try {
        setPlanejamentos(await api.get(`/api/planejamentos?mes=${mes}&ano=${ano}`));
      } catch {
        setPlanejamentos([]);
      }
    };
    fetchPlanejamentos();
  }, [user, mes, ano]);

  // Saldos por banco calculados no servidor; refaz quando os lançamentos mudam
  useEffect(() => {
    if (!user) return;
    const fetchBancos = async () => {
      try {
        setBancos(await api.get('/api/bancos'));
      } catch {
        setBancos([]);
      }
    };
    fetchBancos();
  }, [user, lancamentos]);

  // Totais do mês: movimentações internas (transferências entre bancos e
  // pagamento de fatura) não têm categoria e não são receita/despesa de verdade
  const lancsResumo = lancamentos.filter(l => l.categoria_id);
  const totalReceita = lancsResumo.filter(l => l.tipo_lancamento === 'receita').reduce((acc, l) => acc + Number(l.valor), 0);
  const totalDespesa = lancsResumo.filter(l => l.tipo_lancamento === 'despesa').reduce((acc, l) => acc + Number(l.valor), 0);
  const totalInvestimento = lancsResumo.filter(l => l.tipo_lancamento === 'despesa' && l.categoria?.tipo === 'investimento').reduce((acc, l) => acc + Number(l.valor), 0);
  const saldo = totalReceita - totalDespesa;

  // 50/30/20
  const totalEssenciais = lancsResumo.filter(l => l.tipo_lancamento === 'despesa' && l.categoria?.tipo === 'essencial').reduce((acc, l) => acc + Number(l.valor), 0);
  const totalSuperfluos = lancsResumo.filter(l => l.tipo_lancamento === 'despesa' && l.categoria?.tipo === 'superfluo').reduce((acc, l) => acc + Number(l.valor), 0);
  const limiteEssenciais = totalReceita * 0.5;
  const limiteSuperfluos = totalReceita * 0.3;
  const limiteInvestimento = totalReceita * 0.2;

  // Contas pendentes: atrasadas + vencendo nos próximos 7 dias
  const contasPendentes = ocorrenciasPendentes(contas, pagamentosContas, hojeISO, 7);

  // Orçamento do mês: planejado × gasto por categoria de despesa
  const gastoPorCategoria: { [catId: string]: number } = {};
  lancsResumo.forEach(l => {
    if (l.tipo_lancamento === 'despesa' && l.categoria_id) {
      gastoPorCategoria[l.categoria_id] = (gastoPorCategoria[l.categoria_id] || 0) + Number(l.valor);
    }
  });
  const orcamento = planejamentos
    .filter(p => p.categoria?.tipo_lancamento === 'despesa' && Number(p.valor_planejado) > 0)
    .map(p => {
      const gasto = gastoPorCategoria[p.categoria_id] || 0;
      const planejado = Number(p.valor_planejado);
      return { id: p.categoria_id, nome: p.categoria?.nome || '-', planejado, gasto, pct: (gasto / planejado) * 100 };
    })
    .sort((a, b) => b.pct - a.pct);

  const totalGastos = totalDespesa - totalInvestimento;

  // Orçamento: por padrão só o que pede atenção, ou seja, o que passou do
  // planejado ou está entre 80% e 100%. Exatamente 100% é conta fixa paga no
  // valor previsto, não alerta
  const atencao = orcamento.filter(o => o.pct > 100.05 || (o.pct >= 80 && o.pct < 99.95));
  const orcamentoVisivel = verTodoOrcamento ? orcamento : atencao;

  const totalBancos = bancos.reduce((acc: number, b: any) => acc + Number(b.saldo_atual || 0), 0);
  const esconder = (v: number) => (showBalance ? `R$ ${formatarMoeda(v)}` : 'R$ •••••');

  const pctReceita = (v: number) => (totalReceita ? (v / totalReceita) * 100 : 0);
  const faixas = [
    { nome: 'Essenciais', meta: 'até 50%', valor: totalEssenciais, pct: pctReceita(totalEssenciais), limite: limiteEssenciais, ok: totalEssenciais <= limiteEssenciais, cor: 'teal' },
    { nome: 'Supérfluos', meta: 'até 30%', valor: totalSuperfluos, pct: pctReceita(totalSuperfluos), limite: limiteSuperfluos, ok: totalSuperfluos <= limiteSuperfluos, cor: 'orange' },
    { nome: 'Investimento', meta: 'mín. 20%', valor: totalInvestimento, pct: pctReceita(totalInvestimento), limite: limiteInvestimento, ok: totalInvestimento >= limiteInvestimento, cor: 'green' },
  ];

  // Últimos lançamentos
  const ultimosLanc = lancamentos.slice(0, 5);

  return (
    <Box w="100%" maxW="1000px" mx="auto" p={{ base: 3, md: 8 }} pt={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="center" mb={4} gap={2} wrap="wrap">
        <Heading size="lg">Visão geral</Heading>
        <HStack spacing={2}>
          <Select size="sm" w="auto" value={mes} onChange={e => setMes(Number(e.target.value))} borderRadius="md">
            {meses.map((m, idx) => <option value={idx} key={m}>{m}</option>)}
          </Select>
          <Select size="sm" w="auto" value={ano} onChange={e => setAno(Number(e.target.value))} borderRadius="md">
            {[ano - 1, ano, ano + 1].map(a => <option value={a} key={a}>{a}</option>)}
          </Select>
        </HStack>
      </Flex>

      <VStack spacing={4} align="stretch">
        {/* Resumo do mês: o que entrou, o que foi gasto, o que foi guardado e o que sobrou */}
        <Cartao titulo={`Resumo de ${meses[mes]}`}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacingX={4} spacingY={3}>
            <Box>
              <Text fontSize="xs" color="gray.500">Receitas</Text>
              <Text fontSize="lg" fontWeight="bold" color="teal.600">R$ {formatarMoeda(totalReceita)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Gastos</Text>
              <Text fontSize="lg" fontWeight="bold" color="red.500">R$ {formatarMoeda(totalGastos)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Investido</Text>
              <Text fontSize="lg" fontWeight="bold" color="green.600">R$ {formatarMoeda(totalInvestimento)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Sobra</Text>
              <Text fontSize="lg" fontWeight="bold" color={saldo < 0 ? 'red.500' : 'gray.800'}>R$ {formatarMoeda(saldo)}</Text>
            </Box>
          </SimpleGrid>
        </Cartao>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {/* Saldos: total e cada banco numa linha, sem carrossel */}
          <Cartao
            titulo="Saldos"
            acao={
              <IconButton
                aria-label={showBalance ? 'Ocultar saldos' : 'Mostrar saldos'}
                icon={<Icon as={EyeIcon as any} />}
                onClick={alternarSaldo}
                variant="ghost"
                size="sm"
              />
            }
          >
            <Text fontSize="2xl" fontWeight="bold" color={totalBancos < 0 ? 'red.500' : 'teal.600'} mb={2}>
              {esconder(totalBancos)}
            </Text>
            <VStack align="stretch" spacing={0} divider={<Divider />}>
              {bancos.map((banco: any) => (
                <Flex key={banco.id} justify="space-between" py={2} fontSize="sm" as="button" type="button" textAlign="left"
                  onClick={() => navigate(`/extrato-banco?banco_id=${banco.id}`)}>
                  <Text>{banco.nome}</Text>
                  <Text fontWeight="semibold" color={banco.saldo_atual < 0 ? 'red.500' : undefined}>{esconder(Number(banco.saldo_atual))}</Text>
                </Flex>
              ))}
            </VStack>
          </Cartao>

          {/* Contas a pagar dos próximos 7 dias */}
          {contas.length > 0 && (
            <Cartao titulo="Contas a pagar" acao={<Button size="xs" variant="ghost" colorScheme="teal" onClick={() => navigate('/contas')}>Ver todas</Button>}>
              {contasPendentes.length === 0 ? (
                <Text color="green.600" fontSize="sm">Nada atrasado nem vencendo nos próximos 7 dias ✓</Text>
              ) : (
                <VStack align="stretch" spacing={0} divider={<Divider />}>
                  {contasPendentes.map(oc => (
                    <Flex key={`${oc.conta.id}-${oc.ano}-${oc.mes}`} justify="space-between" align="center" gap={2} py={2}>
                      <Box minW={0}>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{oc.conta.descricao}</Text>
                        <Text fontSize="xs" color="gray.500">vence {formatarDataBR(oc.dataVencimento).slice(0, 5)}</Text>
                      </Box>
                      <Flex align="center" gap={2} flexShrink={0}>
                        <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap">R$ {formatarMoeda(oc.conta.valor)}</Text>
                        {oc.status === 'atrasada' && <Tag size="sm" colorScheme="red">{diasAte(oc.dataVencimento, hojeISO)}d atraso</Tag>}
                        {oc.status === 'vence_hoje' && <Tag size="sm" colorScheme="orange">Hoje</Tag>}
                        {oc.status === 'a_vencer' && <Tag size="sm" colorScheme="blue">em {diasAte(hojeISO, oc.dataVencimento)}d</Tag>}
                      </Flex>
                    </Flex>
                  ))}
                </VStack>
              )}
            </Cartao>
          )}
        </SimpleGrid>

        {/* 50/30/20: uma linha por faixa, com a meta em reais */}
        <Cartao titulo="50/30/20 do mês">
          <Flex>
            <VStack align="stretch" spacing={4} flex="1" w="100%">
              {faixas.map(f => (
                <Box key={f.nome}>
                  <Flex justify="space-between" align="baseline" gap={2}>
                    <Text fontSize="sm" fontWeight="semibold">{f.nome} <Text as="span" fontWeight="normal" color="gray.500">({f.meta})</Text></Text>
                    <Text fontSize="sm" fontWeight="bold" color={f.ok ? undefined : 'red.500'} whiteSpace="nowrap">{f.pct.toFixed(0)}%</Text>
                  </Flex>
                  <Progress
                    mt={1}
                    value={Math.min((f.valor / (f.limite || 1)) * 100, 100)}
                    colorScheme={f.ok ? f.cor : f.nome === 'Investimento' ? 'orange' : 'red'}
                    size="sm"
                    borderRadius="md"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    R$ {formatarMoeda(f.valor)} de R$ {formatarMoeda(f.limite)}
                    {f.nome === 'Investimento'
                      ? (f.ok ? ' · meta atingida ✓' : ` · faltam R$ ${formatarMoeda(f.limite - f.valor)}`)
                      : (f.ok ? '' : ` · passou R$ ${formatarMoeda(f.valor - f.limite)}`)}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Flex>
        </Cartao>

        {/* Orçamento: o que estourou ou está perto; o resto sob demanda */}
        {orcamento.length > 0 && (
          <Cartao
            titulo="Orçamento"
            acao={<Button size="xs" variant="ghost" colorScheme="teal" onClick={() => navigate('/planejamento')}>Planejamento</Button>}
          >
            {atencao.length === 0 && !verTodoOrcamento && (
              <Text fontSize="sm" color="green.600" mb={2}>Nenhuma categoria estourada ou perto do limite ✓</Text>
            )}
            <VStack align="stretch" spacing={3}>
              {orcamentoVisivel.map(o => (
                <Box key={o.id}>
                  <Flex justify="space-between" fontSize="sm" mb={1} gap={2}>
                    <Text fontWeight="medium" noOfLines={1}>{o.nome}</Text>
                    <Text whiteSpace="nowrap" fontWeight="semibold" color={o.pct > 100 ? 'red.500' : o.pct >= 80 ? 'orange.500' : 'gray.500'}>
                      {o.pct.toFixed(0)}%
                    </Text>
                  </Flex>
                  <Progress value={Math.min(o.pct, 100)} colorScheme={o.pct > 100 ? 'red' : o.pct >= 80 ? 'orange' : 'teal'} size="sm" borderRadius="md" />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    R$ {formatarMoeda(o.gasto)} de R$ {formatarMoeda(o.planejado)}
                    {o.pct > 100 ? ` · passou R$ ${formatarMoeda(o.gasto - o.planejado)}` : ` · restam R$ ${formatarMoeda(o.planejado - o.gasto)}`}
                  </Text>
                </Box>
              ))}
            </VStack>
            {orcamento.length > atencao.length && (
              <Button size="sm" variant="link" colorScheme="teal" mt={3} onClick={() => setVerTodoOrcamento(v => !v)}>
                {verTodoOrcamento ? 'Mostrar só o que pede atenção' : `Ver todas as ${orcamento.length} categorias`}
              </Button>
            )}
          </Cartao>
        )}

        {/* Últimos lançamentos: mesmo formato da lista do celular */}
        <Cartao titulo="Últimos lançamentos" acao={<Button size="xs" variant="ghost" colorScheme="teal" onClick={() => navigate('/lancamentos')}>Ver todos</Button>}>
          {ultimosLanc.length === 0 ? (
            <Text fontSize="sm" color="gray.500">Nenhum lançamento neste mês.</Text>
          ) : (
            <VStack align="stretch" spacing={0} divider={<Divider />}>
              {ultimosLanc.map(l => {
                const neutro = !['receita', 'despesa'].includes(l.tipo_lancamento);
                return (
                  <Flex key={l.id} justify="space-between" align="center" gap={3} py={2}>
                    <Box minW={0}>
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{descricaoCurta(l)}</Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {formatarDataBR(String(l.data).slice(0, 10)).slice(0, 5)} · {neutro ? labelTipo(l.tipo_lancamento) : l.categoria?.nome || 'Sem categoria'}
                      </Text>
                    </Box>
                    <Text fontSize="sm" fontWeight="bold" whiteSpace="nowrap" color={corTextoTipo(l.tipo_lancamento)}>
                      {isEntrada(l.tipo_lancamento) ? '+' : ''}R$ {formatarMoeda(Number(l.valor))}
                    </Text>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </Cartao>
      </VStack>
    </Box>
  );
};

export default Dashboard; 